export interface FormIssue {
  field: string;
  message: string;
}

export const fieldPath = (field: string): string =>
  field.replace(/^(body|query|params|headers)(\.|$)/, '').replace(/^\(racine\)$/, '');

const labels: Record<string, string> = {
  title: 'Intitulé',
  name: 'Nom',
  firstName: 'Prénom',
  lastName: 'Nom',
  email: 'E-mail',
  secondaryEmail: 'E-mail secondaire',
  phone: 'Téléphone',
  secondaryPhone: 'Téléphone secondaire',
  password: 'Mot de passe',
  currentPassword: 'Mot de passe actuel',
  newPassword: 'Nouveau mot de passe',
  confirmPassword: 'Confirmation du mot de passe',
  companyId: 'Société',
  contactId: 'Contact',
  candidateId: 'Candidat',
  consultantId: 'Consultant',
  projectId: 'Projet',
  projectName: 'Nom du projet',
  projectMode: 'Rattachement au projet',
  requirementId: 'Besoin',
  positioningId: 'Positionnement',
  missionId: 'Mission',
  timesheetId: 'CRA validé',
  ownerId: 'Responsable',
  sectorId: 'Secteur d’activité',
  statusId: 'Statut',
  stateId: 'État',
  actionTypeId: 'Type d’action',
  subject: 'Objet',
  date: 'Date',
  scheduledTime: 'Heure',
  startDate: 'Date de début',
  endDate: 'Date de fin',
  issueDate: 'Date d’émission',
  dueDate: 'Échéance',
  validUntil: 'Fin de validité',
  year: 'Année',
  month: 'Mois',
  reference: 'Référence',
  origin: 'Origine',
  platform: 'Plateforme',
  location: 'Localisation',
  description: 'Description',
  comment: 'Commentaire',
  comments: 'Commentaires',
  addressLine1: 'Adresse',
  addressLine2: 'Complément d’adresse',
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  region: 'Région',
  address: 'Adresse',
  line1: 'Adresse',
  line2: 'Complément d’adresse',
  shareCapital: 'Capital social',
  rcsNumber: 'Numéro RCS',
  rcsCity: 'Ville du RCS',
  tags: 'Relation commerciale',
  parentCompanyId: 'Société mère',
  priorityId: 'Priorité',
  headcount: 'Nombre de ressources',
  administrativeStatusIds: 'Statuts administratifs attendus',
  seniorityLevelId: 'Séniorité',
  yearsOfExperience: 'Années d’expérience',
  desiredDailyRate: 'TJM souhaité',
  minDailyRate: 'TJM minimum',
  maxDailyRate: 'TJM maximum',
  minEstimatedDailyRate: 'TJM estimé minimum',
  maxEstimatedDailyRate: 'TJM estimé maximum',
  desiredSalary: 'Salaire souhaité',
  minSalary: 'Salaire minimum',
  maxSalary: 'Salaire maximum',
  minEstimatedSalary: 'Salaire estimé minimum',
  maxEstimatedSalary: 'Salaire estimé maximum',
  dailyRate: 'Tarif journalier',
  dailyRateMin: 'TJM minimum',
  dailyRateMax: 'TJM maximum',
  costDailyRate: 'CJM',
  salaryMin: 'Salaire minimum',
  salaryMax: 'Salaire maximum',
  profiles: 'Métiers',
  profileTypeId: 'Métier',
  technologies: 'Technologies',
  skills: 'Compétences',
  skillId: 'Compétence',
  objectives: 'Domaines',
  objectiveId: 'Domaine',
  clearances: 'Habilitations',
  category: 'Catégorie',
  clearanceTypeId: 'Catégorie',
  clearanceNotes: 'Précisions sur les habilitations',
  softSkills: 'Savoir-être',
  levelId: 'Niveau',
  importanceId: 'Importance',
  label: 'Libellé',
  languages: 'Langues',
  languageId: 'Langue',
  trainings: 'Formations',
  certifications: 'Certifications',
  experiences: 'Expériences',
  lines: 'Prestations',
  quantity: 'Quantité',
  unitPrice: 'Prix unitaire',
  vatRate: 'TVA',
  totalExclTax: 'Montant HT',
  totalInclTax: 'Montant TTC',
  amountExclTax: 'Montant HT',
  amountInclTax: 'Montant TTC',
  file: 'Fichier',
  fileId: 'Document',
  parentDocumentId: 'Document lié',
  parties: 'Parties du contrat',
  companyIds: 'Sociétés',
  linkedIn: 'LinkedIn',
  url: 'Lien',
  jobTitleId: 'Poste',
  service: 'Service',
  interview: 'Entretien',
  report: 'Compte rendu',
  summary: 'Synthèse',
  opinion: 'Avis',
  roleId: 'Rôle',
  roleIds: 'Rôles',
  teamId: 'Équipe',
  permissions: 'Permissions',
};

/** Keeps row numbers: “Habilitations · 2 · Catégorie”, never a technical API path. */
export function fieldLabel(field: string, overrides: Record<string, string> = {}): string {
  const path = fieldPath(field);
  if (overrides[path]) return overrides[path];
  const parts = path.split('.').filter((part) => part && part !== 'root');
  const translated = parts.map((part) =>
    /^\d+$/.test(part) ? String(Number(part) + 1) : (overrides[part] ?? labels[part]),
  );
  return translated.filter(Boolean).join(' · ') || 'Formulaire';
}

/** Accepts RHF errors, including field arrays, without traversing refs/DOM nodes. */
export function flattenFormErrors(errors: unknown, prefix = ''): FormIssue[] {
  if (!errors || typeof errors !== 'object') return [];
  const value = errors as Record<string, unknown>;
  const own = typeof value.message === 'string' ? [{ field: prefix, message: value.message }] : [];
  return [
    ...own,
    ...Object.entries(value).flatMap(([key, child]) =>
      ['message', 'type', 'types', 'ref'].includes(key)
        ? []
        : flattenFormErrors(child, [prefix, key].filter(Boolean).join('.')),
    ),
  ];
}

export function describeIssues(issues: FormIssue[]): string {
  return [...new Set(issues.map((issue) => `${fieldLabel(issue.field)} : ${issue.message}`))].join(
    '\n',
  );
}
